import { QueryClientProvider } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useAnalyticsBootstrap } from '@/lib/analytics';
import { createQueryClient } from '@/lib/api/query-client';
import { AuthBootstrap } from '@/lib/auth';
import { useConsentHydration } from '@/lib/consent/store';
import { useGtmConsentBootstrap } from '@/lib/gtm/consent';
import { useErrorReporting } from '@/lib/monitoring';
import { useQualityDetection } from '@/lib/quality';
import { useWishlistSync } from '@/lib/wishlist';
import { useMotionReady } from '@/motion/ready';

/**
 * Client-side bootstrap of the app-wide stores. Every effect runs after the first render, so the
 * server/first-client render is deterministic (SSR- and hydration-safe):
 *   quality tier (+ `<html data-q>`) · stored consent choice ·
 *   the on-device wishlist (merged into the account at sign-in) ·
 *   the analytics consent listener (loads nothing until a provider is configured and Accept was
 *   pressed) · Google Tag Manager's Consent Mode updates (a build with `VITE_GTM_ID` only) ·
 *   the global error handlers (the reporter loads on the first error).
 */
function StoreBootstrap() {
  useQualityDetection();
  useConsentHydration();
  useAnalyticsBootstrap();
  useGtmConsentBootstrap();
  useErrorReporting();
  useWishlistSync();
  useMotionReady();
  // No smooth-scroll library any more (D-372): the page scrolls natively, which is what an enterprise site does and what
  // lets every link open a page from its top (a glide that kept running to its old target undid the jump to the top).
  return null;
}

export interface AppProvidersProps {
  children: ReactNode;
  /**
   * The query cache to use. The pre-render and the hydrating client pass theirs (warmed with what
   * the page reads); everything else gets a fresh one.
   */
  queryClient?: QueryClient;
}

/**
 * App providers: TanStack Query (staleTime 60 s, retry once except on 4xx), the zustand-backed stores
 * (quality, consent, auth — they need no provider) and their bootstrap.
 * The QueryClient is created per mount, never at module level, so it is safe to reuse for SSR.
 *
 * The pre-render renders exactly this tree, so the server markup and the first client render agree
 * on every generated `useId` (ids depend on the shape of the tree above them).
 */
export function AppProviders({ children, queryClient }: AppProvidersProps) {
  const [client] = useState(() => queryClient ?? createQueryClient());

  return (
    <QueryClientProvider client={client}>
      <StoreBootstrap />
      <AuthBootstrap />
      {children}
    </QueryClientProvider>
  );
}
